import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Popup, Source, Layer, MapRef } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import type { FeatureCollection, Polygon } from 'geojson';
import { atom, useAtomValue } from 'jotai';

import { Map as MapComponent } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import type { MapUserConfig } from 'components/map/types';
import mapStyles from 'components/map/map.module.scss';
import { OwnershipGraph } from './types';
import { getNodes } from './data-helpers';
import styles from './ownership-map.module.scss';
import { hoveredNodeIdAtom, useSetHoveredNodeIdThrottled } from './hover-state';

/** Local geography shape used by map components. */
export interface MapGeography {
  id: number;
  referenceLocationName: string;
  searchText?: string;
  geometryJson: GeoJSON.Geometry | null;
}

interface OwnershipMapProps {
  ownershipGraph: OwnershipGraph;
  geographies?: MapGeography[];
  selectedNodeId?: string | null;
}

export function OwnershipMap({
  ownershipGraph,
  geographies = [],
  selectedNodeId,
}: OwnershipMapProps) {
  const hoveredNodeId = useAtomValue(hoveredNodeIdAtom);
  const setHoveredNodeId = useSetHoveredNodeIdThrottled();
  const mapRefAtom = useMemo(() => atom<MapRef | null>(null), []);
  const mapRefValue = useAtomValue(mapRefAtom);
  const mapRef = useRef<MapRef | null>(null);
  mapRef.current = mapRefValue;
  const containerRef = useRef<HTMLDivElement>(null);

  // Resize the Mapbox canvas when the container dimensions change
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      mapRef.current?.getMap()?.resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const mapConfigAtom = useMemo(() => {
    const config = getMapDefaultConfig();
    return atom<MapUserConfig>({
      ...config,
      wellsV2: { ...config.wellsV2, checked: false },
      developmentAreas: { ...config.developmentAreas, checked: false },
      titleEntityAssets: { ...config.titleEntityAssets, checked: false },
      basinTier: { ...config.basinTier, checked: false },
      pipelines: { ...config.pipelines, checked: false },
      drillingRigs: { ...config.drillingRigs, checked: false },
      focusAreas: { ...config.focusAreas, checked: false },
      scrollZoom: { checked: true },
      popups: { checked: false },
    });
  }, []);

  const [hoveredGeoName, setHoveredGeoNameRaw] = useState<string | null>(null);
  const [hoverLngLat, setHoverLngLat] = useState<{
    lng: number;
    lat: number;
  } | null>(null);

  // Build GeoJSON FeatureCollection from geographies
  const geoFeatures = useMemo((): FeatureCollection<Polygon> => {
    const features = geographies
      .filter((g) => g.geometryJson)
      .map((g) => ({
        type: 'Feature' as const,
        properties: { id: g.id, name: g.referenceLocationName },
        geometry: g.geometryJson as Polygon,
      }));
    return { type: 'FeatureCollection', features };
  }, [geographies]);

  // geometryName -> [geoId, ...] — O(1) lookup instead of per-hover scan
  const geoIdsByName = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const g of geographies) {
      if (!g.geometryJson) continue;
      const arr = map.get(g.referenceLocationName);
      if (arr) arr.push(g.id);
      else map.set(g.referenceLocationName, [g.id]);
    }
    return map;
  }, [geographies]);

  // geometryName -> first leaf node with non-zero interest (for map-hover->table sync)
  const nodeIdByGeoName = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of getNodes(ownershipGraph)) {
      const name = node.property.volume.geometryName;
      if (!name || map.has(name)) continue;
      if (!node.interests.some((i) => i.fractionF > 0)) continue;
      map.set(name, String(node.id));
    }
    return map;
  }, [ownershipGraph]);

  // Active node = hovered takes priority, then selected
  const activeNodeId = hoveredNodeId ?? selectedNodeId ?? null;

  // Geo IDs to highlight for the active node — applied via feature-state
  // in the effect below.
  const highlightGeoIds = useMemo(() => {
    if (!activeNodeId) return [] as number[];
    const node = ownershipGraph.nodes[activeNodeId];
    if (!node) return [] as number[];
    const geoName = node.property.volume.geometryName;
    if (!geoName) return [] as number[];
    return geoIdsByName.get(geoName) ?? [];
  }, [activeNodeId, ownershipGraph, geoIdsByName]);

  // Sync highlight via Mapbox feature-state — O(1) per feature vs filter
  // evaluation which runs against all 4000+ features on every change.
  const prevHighlightIdsRef = useRef<number[]>([]);
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const sourceId = 'ownership-geos';
    const apply = () => {
      if (!map.getSource(sourceId)) return false;
      for (const id of prevHighlightIdsRef.current) {
        map.setFeatureState({ source: sourceId, id }, { highlighted: false });
      }
      for (const id of highlightGeoIds) {
        map.setFeatureState({ source: sourceId, id }, { highlighted: true });
      }
      prevHighlightIdsRef.current = highlightGeoIds;
      return true;
    };
    if (apply()) return;
    const onSourceData = (e: {
      sourceId?: string;
      isSourceLoaded?: boolean;
    }) => {
      if (e.sourceId === sourceId && e.isSourceLoaded && apply()) {
        map.off('sourcedata', onSourceData);
      }
    };
    map.on('sourcedata', onSourceData);
    return () => {
      map.off('sourcedata', onSourceData);
    };
  }, [highlightGeoIds]);

  const setHoveredGeoName = useCallback(
    (name: string | null) => {
      setHoveredGeoNameRaw(name);
      setHoveredNodeId(name ? (nodeIdByGeoName.get(name) ?? null) : null);
    },
    [setHoveredNodeId, nodeIdByGeoName]
  );

  // Compute bounds for initial map view
  const mapBounds = useMemo(() => {
    const coords: [number, number][] = [];
    for (const geo of geographies) {
      if (!geo.geometryJson || geo.geometryJson.type !== 'Polygon') continue;
      for (const ring of (geo.geometryJson as GeoJSON.Polygon).coordinates) {
        for (const pt of ring) {
          coords.push(pt as [number, number]);
        }
      }
    }
    if (coords.length === 0) return null;
    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    return { minLng, maxLng, minLat, maxLat };
  }, [geographies]);

  const initialCenter = useMemo(() => {
    if (!mapBounds) return { longitude: -101.5, latitude: 32.0, zoom: 12 };
    return {
      longitude: (mapBounds.minLng + mapBounds.maxLng) / 2,
      latitude: (mapBounds.minLat + mapBounds.maxLat) / 2,
      zoom: 12,
    };
  }, [mapBounds]);

  // Fit map to geographies once the map ref is available
  const hasGeoBounds = !!mapBounds;
  useEffect(() => {
    if (!mapBounds || !mapRef.current) return;
    const map = mapRef.current.getMap();
    if (map) {
      map.fitBounds(
        [
          [mapBounds.minLng, mapBounds.minLat],
          [mapBounds.maxLng, mapBounds.maxLat],
        ],
        { padding: 40, duration: 0 }
      );
    }
  }, [hasGeoBounds]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className={styles.mapContainer}
      onMouseLeave={() => {
        setHoveredGeoName(null);
        setHoverLngLat(null);
      }}
    >
      <MapComponent
        longitude={initialCenter.longitude}
        latitude={initialCenter.latitude}
        zoom={initialCenter.zoom}
        mapConfigAtom={mapConfigAtom}
        showControls
        showMapFilters
        blockPopups
        disableWellsLayer
        refAtom={mapRefAtom}
        interactiveLayerIds={['geo-fill']}
        cursor={hoveredGeoName ? 'pointer' : ''}
        onMouseMove={(evt: MapLayerMouseEvent) => {
          const map = mapRef.current?.getMap();
          if (!map || !map.getLayer('geo-fill')) return;
          const features = map.queryRenderedFeatures(evt.point, {
            layers: ['geo-fill'],
          });
          const name = (features[0]?.properties?.name as string) ?? null;
          setHoveredGeoName(name);
          setHoverLngLat(
            name ? { lng: evt.lngLat.lng, lat: evt.lngLat.lat } : null
          );
        }}
        onMouseLeave={() => {
          setHoveredGeoName(null);
          setHoverLngLat(null);
        }}
      >
        {/* All geographies -- cool gray fill + outline.
            Highlight is driven by feature-state (set imperatively in the
            effect above) so hover transitions don't force Mapbox to rebuild
            tile data. promoteId makes `properties.id` the feature ID used by
            setFeatureState. */}
        <Source
          id="ownership-geos"
          type="geojson"
          data={geoFeatures}
          promoteId="id"
        >
          <Layer
            id="geo-fill"
            type="fill"
            paint={{ 'fill-color': '#878d96', 'fill-opacity': 0.25 }}
          />
          <Layer
            id="geo-outline"
            type="line"
            paint={{
              'line-color': '#697077',
              'line-opacity': 0.6,
              'line-width': 2,
            }}
          />
          <Layer
            id="geo-highlight-fill"
            type="fill"
            paint={{
              'fill-color': '#0f62fe',
              'fill-opacity': [
                'case',
                ['boolean', ['feature-state', 'highlighted'], false],
                0.2,
                0,
              ],
            }}
          />
          <Layer
            id="geo-highlight-outline"
            type="line"
            paint={{
              'line-color': '#0f62fe',
              'line-width': [
                'case',
                ['boolean', ['feature-state', 'highlighted'], false],
                3,
                0,
              ],
            }}
          />
        </Source>
        {hoveredGeoName && hoverLngLat && (
          <Popup
            longitude={hoverLngLat.lng}
            latitude={hoverLngLat.lat}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            className={mapStyles.popup}
          >
            <p>{hoveredGeoName}</p>
          </Popup>
        )}
      </MapComponent>
    </div>
  );
}
